'use client';

import { forwardRef, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { Slot } from '../slot';
import { PageContext } from './context/page-context';
import { pageRootStyles } from './styles/page-root.styles';
import type { PageRootPropsInterface } from './types/page-root.types';

/**
 * Page Root is a generic container to house all Page subcomponents
 *
 * @since 8.15.0
 */
export const PageRoot = forwardRef<HTMLDivElement, PageRootPropsInterface>(
  (props: PageRootPropsInterface, forwardedRef) => {
    const { asChild = false, fluid = false, children, className, css: cssProp, ...rest } = props;

    const styles = css(pageRootStyles, cssProp);

    const Component = asChild ? Slot : 'div';

    const contextValue = useMemo(() => ({ fluid }), [fluid]);

    return (
      <PageContext.Provider value={contextValue}>
        <Component
          data-c11n-component="Page.Root"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', styles, className)}
          ref={forwardedRef}
          {...rest}
        >
          {children}
        </Component>
      </PageContext.Provider>
    );
  },
);

PageRoot.displayName = 'Page.Root';
